import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { WeekPlan } from '@content-planner/shared';
import { ContentProfile as ContentProfileSchema } from '@content-planner/shared';
import { draftStorage } from '../lib/storage.js';
import { api, ApiClientError } from '../lib/api.js';
import { useGenerateMonth } from '../hooks/useGeneratePlan.js';
import { WeekDisplay } from '../components/plan/WeekDisplay.js';
import { LeadCaptureGate } from '../components/plan/LeadCaptureGate.js';

type Lead = { email: string; name?: string };

export function PlanPage() {
  const draft = useMemo(() => draftStorage.load(), []);
  const parsedProfile = useMemo(() => {
    if (!draft?.profile) return null;
    const r = ContentProfileSchema.safeParse(draft.profile);
    return r.success ? r.data : null;
  }, [draft]);
  const month = draft?.month;

  const plan = useGenerateMonth();
  const [token, setToken] = useState<string | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const generateRequested = useRef(false);
  const finalizeStarted = useRef(false);

  function handleUnlock(t: string, l: Lead) {
    setToken(t);
    setLead(l);
  }

  // Generate the full month once on first mount.
  useEffect(() => {
    if (!parsedProfile || !month || generateRequested.current) return;
    generateRequested.current = true;
    void plan.generate({ profile: parsedProfile, month });
  }, [parsedProfile, month, plan]);

  // Once the lead is captured and the full month is in hand, finalize: render the
  // PDF, store the plan, and email the link. The whole month was generated up
  // front, so there is no second generation call — the model had full-month
  // context when it wrote every week.
  useEffect(() => {
    if (finalizeStarted.current) return;
    if (!token || !lead || !parsedProfile || !month) return;
    const weeks = plan.weeks;
    if (!weeks || weeks.length !== 4) return;

    finalizeStarted.current = true;
    (async () => {
      try {
        await api.finalize(
          {
            email: lead.email,
            name: lead.name,
            plan: { profile: parsedProfile, month, weeks: weeks as WeekPlan[] },
          },
          token,
        );
      } catch (err) {
        finalizeStarted.current = false;
        setFinalizeError(
          err instanceof ApiClientError
            ? err.message
            : "We couldn't email your plan. Reply to the confirmation email and we'll send it manually.",
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, lead, parsedProfile, month, plan.weeks]);

  if (!parsedProfile || !month) {
    return <Navigate to="/plan" replace />;
  }

  const week1Plan = plan.weeks?.find((w) => w.week === 1);

  return (
    <div className="pt-2">
      <span className="gcm-eyebrow">Your plan</span>
      <h2 className="mt-1 mb-1">Content plan for {month}</h2>
      <p className="text-text-secondary mb-6">
        Mon, Wed, and Fri each week. One sharp idea per slot, tailored to your business.
      </p>

      <WeekDisplay
        week={1}
        month={month}
        plan={week1Plan}
        loading={plan.status === 'loading' && !week1Plan}
      />

      {plan.status === 'error' && !week1Plan && (
        <p className="text-sm text-danger mb-4">{plan.error}</p>
      )}

      {week1Plan && !token && (
        <LeadCaptureGate profile={parsedProfile} month={month} onUnlock={handleUnlock} />
      )}

      {token && lead && (
        <ThankYouPanel name={lead.name} email={lead.email} finalizeError={finalizeError} />
      )}
    </div>
  );
}

type ThankYouProps = {
  name?: string;
  email: string;
  finalizeError: string | null;
};

function ThankYouPanel({ name, email, finalizeError }: ThankYouProps) {
  return (
    <aside className="gcm-card border-2 border-gcm-brown my-6">
      <span className="gcm-eyebrow">Thanks{name ? `, ${name}` : ''}</span>
      <h3 className="mt-1 mb-2">Your complete content plan is on the way!</h3>
      <p className="text-text-secondary mb-2">
        The full month, all four weeks, will land in your inbox at{' '}
        <span className="font-semibold text-text-primary">{email}</span> in a few minutes — with a
        PDF and a link you can come back to anytime.
      </p>
      <p className="text-text-secondary">You can close this tab.</p>

      {finalizeError && <p className="text-sm text-danger mt-4">{finalizeError}</p>}
    </aside>
  );
}
